import { __rest } from 'tslib';
import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import { Tooltip as Tooltip$1, Chip, Popper, ClickAwayListener, Box, Typography, LinearProgress, Snackbar, Grow } from '@mui/material';
import { makeG2STyles, useTheme } from '@komune-io/g2-themes';
import { cx } from '@emotion/css';
import { CloseRounded, ReportProblemOutlined, CheckRounded, InfoOutlined } from '@mui/icons-material';
import { useSnackbar, SnackbarProvider, enqueueSnackbar } from 'notistack';
export { closeSnackbar as closeAlert } from 'notistack';

const useStyles$3 = makeG2STyles()((theme) => ({
    root: {
        background: theme.colors.primary,
        fontSize: '13px',
        padding: theme.spacing,
        boxShadow: theme.shadows[1]
    },
    arrow: {
        color: theme.colors.primary,
        width: '16px !important',
        height: '12px !important',
        marginTop: '-11px !important',
        '&::before': {
            borderRadius: '2px'
        }
    }
}));
const TooltipBase = (props, ref) => {
    const { children, helperText, style, className, id, classes, open } = props, other = __rest(props, ["children", "helperText", "style", "className", "id", "classes", "open"]);
    const defaultStyles = useStyles$3();
    return (React.createElement(Tooltip$1, Object.assign({ ref: ref, id: id, className: defaultStyles.cx('AruiTooltip-root', className), style: style, arrow: true, classes: Object.assign(Object.assign({}, classes), { tooltip: defaultStyles.classes.root, arrow: defaultStyles.classes.arrow }) }, other, { open: open, title: helperText, placement: 'bottom' }), children));
};
const Tooltip = forwardRef(TooltipBase);

const StatusTagBase = (props, ref) => {
    const { label, variant = 'info', customColor, color, className } = props, other = __rest(props, ["label", "variant", "customColor", "color", "className"]);
    const theme = useTheme();
    const colorSelection = useMemo(() => {
        if (customColor || color)
            return color !== null && color !== void 0 ? color : customColor;
        return theme.colors[variant];
    }, [variant, customColor, color, theme]);
    return (React.createElement(Chip, Object.assign({ ref: ref, label: label, className: cx('AruiStatusTag-root', className), sx: {
            width: 'fit-content',
            '& .MuiChip-label': {
                color: colorSelection,
                WebkitLineClamp: 2,
                lineClamp: '2',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden'
            },
            background: `${colorSelection}26`,
            border: `1.5px solid ${colorSelection}`,
            height: 'unset',
            padding: '5px',
            borderRadius: '200px'
        } }, other)));
};
const StatusTag = forwardRef(StatusTagBase);

const useStyles$2 = makeG2STyles()((theme) => ({
    popper: {
        borderRadius: theme.borderRadius,
        background: 'white',
        zIndex: 2000,
        padding: theme.spacing * 3,
        boxShadow: theme.shadows[3],
        '&[data-popper-placement="bottom"]': {
            marginTop: '25px !important',
            '& .AruiPopover-arrowContainer': {
                top: 0,
                left: 0,
                marginTop: '-25px',
                marginLeft: '-20px'
            }
        },
        '&[data-popper-placement="top"]': {
            marginBottom: '25px !important',
            '& .AruiPopover-arrow': {
                transform: 'rotate(180deg)'
            },
            '& .AruiPopover-arrowContainer': {
                bottom: 0,
                left: 0,
                marginLeft: '-20px'
            }
        },
        '&[data-popper-placement="right"]': {
            marginLeft: '25px !important',
            '& .AruiPopover-arrow': {
                transform: 'rotate(270deg)'
            },
            '& .AruiPopover-arrowContainer': {
                left: 0,
                marginLeft: '-32px',
                marginTop: '-12px'
            }
        },
        '&[data-popper-placement="left"]': {
            marginRight: '25px !important',
            '& .AruiPopover-arrow': {
                transform: 'rotate(90deg)'
            },
            '& .AruiPopover-arrowContainer': {
                right: 0,
                marginRight: '8px',
                marginTop: '-12px'
            }
        }
    },
    closeIcon: {
        position: 'absolute',
        top: 10,
        right: 10,
        cursor: 'pointer',
        width: 20,
        height: 20,
        color: '#676879'
    },
    arrow: {
        position: 'absolute',
        fontSize: 7,
        width: '40px',
        height: '25px',
        color: 'white',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            margin: 'auto',
            display: 'block',
            background: 'white',
            width: '100%',
            height: '100%',
            borderStyle: 'solid',
            transformOrigin: '0 100%',
            borderRadius: '3px',
            transform: 'rotate(45deg)',
            boxShadow: theme.shadows[1]
        }
    }
}));
const PopoverBase = (props, ref) => {
    const { children, open = false, closeOnClickAway = false, onClose, className, style, id, classes, styles } = props, other = __rest(props, ["children", "open", "closeOnClickAway", "onClose", "className", "style", "id", "classes", "styles"]);
    const defaultStyles = useStyles$2();
    const [arrow, setArrow] = useState(undefined);
    const handleClickAway = useCallback(() => {
        if (closeOnClickAway && onClose && open) {
            onClose();
        }
    }, [closeOnClickAway, onClose, open]);
    const setArrowRef = useCallback((ref) => setArrow(ref), []);
    const modifiers = useMemo(() => [
        {
            name: 'arrow',
            enabled: true,
            options: {
                element: arrow,
                padding: 10
            }
        }
    ], [arrow]);
    const popper = (React.createElement(Popper, Object.assign({ ref: ref, id: id, className: defaultStyles.cx(defaultStyles.classes.popper, 'AruiPopover-root', className), style: style, open: open, modifiers: modifiers }, other),
        React.createElement("div", { ref: setArrowRef, className: 'AruiPopover-arrowContainer' },
            React.createElement("div", { className: defaultStyles.cx(defaultStyles.classes.arrow, 'AruiPopover-arrow', classes === null || classes === void 0 ? void 0 : classes.arrow), style: styles === null || styles === void 0 ? void 0 : styles.arrow })),
        onClose && (React.createElement(CloseRounded, { className: defaultStyles.cx(defaultStyles.classes.closeIcon, 'AruiPopover-closeIcon', classes === null || classes === void 0 ? void 0 : classes.closeIcon), style: styles === null || styles === void 0 ? void 0 : styles.closeIcon, onClick: onClose })),
        children));
    if (closeOnClickAway && open)
        return (React.createElement(ClickAwayListener, { onClickAway: handleClickAway }, popper));
    return popper;
};
const Popover = forwardRef(PopoverBase);

const useStyles$1 = makeG2STyles()((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center'
    },
    progress: {
        height: 8,
        borderRadius: 4,
        backgroundColor: '#DDE1E6',
        flexGrow: 1
    },
    bar: {
        borderRadius: 4
    },
    label: {
        color: theme.colors.secondary,
        marginRight: theme.spacing / 2
    }
}));
const ProgressIndicator = (props) => {
    const { value = 0, className, style, id, classes, styles, label } = props, other = __rest(props, ["value", "className", "style", "id", "classes", "styles", "label"]);
    const defaultStyles = useStyles$1();
    const roundedValue = useMemo(() => Math.round(value), [value]);
    return (React.createElement(Box, { className: defaultStyles.cx(defaultStyles.classes.root, 'AruiProgressIndicator-root', className), style: style, id: id },
        React.createElement(Typography, { variant: 'subtitle1', className: defaultStyles.cx(defaultStyles.classes.label, 'AruiProgressIndicator-label', classes === null || classes === void 0 ? void 0 : classes.label), style: styles === null || styles === void 0 ? void 0 : styles.label }, label !== null && label !== void 0 ? label : `${roundedValue}%`),
        React.createElement(LinearProgress, Object.assign({ variant: 'determinate', color: 'secondary', className: defaultStyles.cx(defaultStyles.classes.progress, 'AruiProgressIndicator-LinearProgress', classes === null || classes === void 0 ? void 0 : classes.linearProgress), classes: { bar: defaultStyles.classes.bar }, style: styles === null || styles === void 0 ? void 0 : styles.linearProgress, value: value }, other))));
};

const useStyles = makeG2STyles()((theme) => ({
    root: {
        maxWidth: '600px',
        width: 'fit-content'
    },
    relativeRoot: {
        position: 'relative',
        transform: 'unset',
        left: 0,
        bottom: 0
    },
    content: {
        background: '#595959',
        borderRadius: theme.borderRadius,
        position: 'relative',
        overflow: 'hidden',
        padding: '4px 32px 4px 60px',
        width: '100%',
        '& .MuiSnackbarContent-message': {
            padding: '3px 0',
            minHeight: '29px',
            display: 'flex',
            alignItems: 'center'
        }
    },
    contentWithoutClose: {
        padding: '4px 16px 4px 60px'
    },
    lightRoot: {
        background: 'white',
        color: '#676879',
        '& .MuiSnackbarContent-message': {
            color: '#676879'
        }
    },
    closeIcon: {
        position: 'absolute',
        top: 'calc(50% - 9.5px)',
        right: 10,
        cursor: 'pointer',
        width: 20,
        height: 20
    },
    closeIconLight: {
        color: '#676879'
    },
    severityIcon: {
        width: 30,
        height: 30,
        color: 'rgba(255, 255, 255, 0.8)'
    },
    severityIndicator: {
        backgroundColor: theme.colors.info,
        position: 'absolute',
        height: '100%',
        width: '50px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        top: 0,
        left: 0
    },
    severityIndicatorError: {
        backgroundColor: theme.colors.error
    },
    severityIndicatorWarning: {
        backgroundColor: theme.colors.warning
    },
    severityIndicatorSuccess: {
        backgroundColor: theme.colors.success
    }
}));
const AlertBase = (props, ref) => {
    const { children, onClose, severity = 'info', colorBase = 'dark', className, open = true, message, classes, styles, isRelative = false } = props, other = __rest(props, ["children", "onClose", "severity", "colorBase", "className", "open", "message", "classes", "styles", "isRelative"]);
    const defaultStyles = useStyles();
    const severityIcon = useMemo(() => {
        if (severity === 'warning')
            return (React.createElement(ReportProblemOutlined, { className: defaultStyles.cx(defaultStyles.classes.severityIcon, classes === null || classes === void 0 ? void 0 : classes.severityIcon), style: styles === null || styles === void 0 ? void 0 : styles.severityIcon }));
        if (severity === 'success')
            return (React.createElement(CheckRounded, { className: defaultStyles.cx(defaultStyles.classes.severityIcon, classes === null || classes === void 0 ? void 0 : classes.severityIcon), style: styles === null || styles === void 0 ? void 0 : styles.severityIcon }));
        if (severity === 'error')
            return (React.createElement(CloseRounded, { className: defaultStyles.cx(defaultStyles.classes.severityIcon, classes === null || classes === void 0 ? void 0 : classes.severityIcon), style: styles === null || styles === void 0 ? void 0 : styles.severityIcon }));
        return (React.createElement(InfoOutlined, { className: defaultStyles.cx(defaultStyles.classes.severityIcon, classes === null || classes === void 0 ? void 0 : classes.severityIcon), style: styles === null || styles === void 0 ? void 0 : styles.severityIcon }));
    }, [severity, classes === null || classes === void 0 ? void 0 : classes.severityIcon, styles === null || styles === void 0 ? void 0 : styles.severityIcon]);
    return (React.createElement(Snackbar, Object.assign({ className: defaultStyles.cx(defaultStyles.classes.root, isRelative && defaultStyles.classes.relativeRoot, 'AruiAlert-root', className), ref: ref, ClickAwayListenerProps: { onClickAway: () => { } }, ContentProps: {
            className: defaultStyles.cx(defaultStyles.classes.content, colorBase === 'light' && defaultStyles.classes.lightRoot, !onClose && defaultStyles.classes.contentWithoutClose, 'AruiAlert-contentContainer', classes === null || classes === void 0 ? void 0 : classes.contentContainer),
            style: styles === null || styles === void 0 ? void 0 : styles.contentContainer
        }, message: React.createElement(React.Fragment, null,
            React.createElement(Box, { className: defaultStyles.cx(defaultStyles.classes.severityIndicator, severity === 'error' &&
                    defaultStyles.classes.severityIndicatorError, severity === 'success' &&
                    defaultStyles.classes.severityIndicatorSuccess, severity === 'warning' &&
                    defaultStyles.classes.severityIndicatorWarning, classes === null || classes === void 0 ? void 0 : classes.severityIndicator), style: styles === null || styles === void 0 ? void 0 : styles.severityIndicator }, severityIcon),
            children ? (children) : (React.createElement(Typography, { sx: { color: 'currentColor' }, variant: 'body2' }, message)),
            onClose && (React.createElement(CloseRounded, { className: defaultStyles.cx(defaultStyles.classes.closeIcon, colorBase === 'light' && defaultStyles.classes.closeIconLight, 'AruiAlert-closeIcon', classes === null || classes === void 0 ? void 0 : classes.closeIcon), style: styles === null || styles === void 0 ? void 0 : styles.closeIcon, onClick: onClose }))), open: true, onClose: onClose }, other)));
};
const Alert = forwardRef(AlertBase);

const useSytles = makeG2STyles()({
    alert: {
        position: 'relative',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    }
});
const AlertHub = (props) => {
    const { children } = props, other = __rest(props, ["children"]);
    return (React.createElement(SnackbarProvider, Object.assign({ anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        }, Components: {
            G2Alert: ClosableAlert
        }, maxSnack: 3, TransitionComponent: Grow }, other), children));
};
const ClosableAlert = forwardRef((props, ref) => {
    /* eslint-disable */
    const { key, id = key, className, persist, autoHideDuration, hideIconVariant, iconVariant } = props, other = __rest(props
    /* eslint-enable */
    , ["key", "id", "className", "persist", "autoHideDuration", "hideIconVariant", "iconVariant"]);
    /* eslint-enable */
    const { classes, cx } = useSytles();
    const { closeSnackbar } = useSnackbar();
    const handleClose = useCallback(() => closeSnackbar(id), [id]);
    return (React.createElement(Alert, Object.assign({ key: key, id: id, className: cx(classes.alert, className), onClose: handleClose, ref: ref }, other)));
});

const useAlertHub = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const pushAlert = useCallback((alertProps) => {
        var _a, _b;
        //@ts-ignore
        return enqueueSnackbar((_a = alertProps.message) !== null && _a !== void 0 ? _a : '', Object.assign({ variant: 'G2Alert', persist: (_b = alertProps.persist) !== null && _b !== void 0 ? _b : true }, alertProps));
    }, [enqueueSnackbar]);
    return {
        pushAlert: pushAlert,
        closeAlert: closeSnackbar
    };
};
const pushAlert = (alertProps) => {
    var _a, _b;
    //@ts-ignore
    return enqueueSnackbar((_a = alertProps.message) !== null && _a !== void 0 ? _a : '', Object.assign({ variant: 'G2Alert', persist: (_b = alertProps.persist) !== null && _b !== void 0 ? _b : true }, alertProps));
};

export { Alert, AlertHub, Popover, ProgressIndicator, StatusTag, Tooltip, pushAlert, useAlertHub };
//# sourceMappingURL=index.es.js.map
